import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import useSWR from "swr";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useLocalStorage } from "usehooks-ts";

import CustomCard from "components/CustomCard";
import { Loader } from "components/customElements";
import DiscussionComposeModal from "components/DiscussionComposeModal";
import DiscussionRestrictionState from "components/DiscussionRestrictionState";
import UnverifiedPhoneNumber from "components/DiscussionRestrictionState/UnverifiedPhone";
import DiscussionSnippet from "components/DiscussionSnippet";
import SwitchButton from "components/SwitchButton";
import { Tab, Tabs } from "components/Tabs";
import useActivityTracker from "hooks/useActivityTracker";
import useAuthentication from "hooks/useAuthentication";
import { useEffectOnce } from "hooks/useEffectOnce";
import { useGlobalState } from "hooks/useGlobalState";
import useInfiniteList from "hooks/useInfiniteList";
import useMediaQuery from "hooks/useMediaQuery";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";

import { type Tab as TabType, tabs } from "../DiscussionsPageTabs";

const DiscussionModal = dynamic(() => import("components/DiscussionModal"));

export const discussionsUrl = (
  pageIndex: number,
  filter?: string,
  options?: {
    excludeBots?: boolean;
  }
) => {
  const url = new ApiEndpoint("/api/v3/comments");

  if (filter && filter !== "all") {
    url.searchParams.append(filter, "true");
  }

  if (options?.excludeBots) {
    url.searchParams.append("exclude-bots", "true");
  }

  url.searchParams.append("page[number]", `${pageIndex}`);
  url.searchParams.append("page[size]", "12");
  url.searchParams.append("root-with-latest-reply", "true");

  return url.href;
};

type Props = {};

const DiscussionPageBrowse = ({}: Props) => {
  const { mobile } = useMediaQuery();
  const { account: loggedInAccount, interfaceSetting } = useAuthentication();

  const { data: phoneNumber, isValidating: isPhoneNumberValidating } = useSWR(
    loggedInAccount?.id
      ? `/api/v2/accounts/${loggedInAccount?.id}/phone-number`
      : null,
    baseFetcher
  );

  const { trackActivity } = useActivityTracker();

  const [excludeBots, setExcludeBots] = useLocalStorage<any>(
    "excludeBots",
    true
  );

  const router = useRouter();
  const tab = router.query.tab as string;
  const [isCompactView, _setIsCompactView] = useState(true);

  const [showDiscussionComposeModal, setShowDiscussionComposeModal] =
    useGlobalState("showDiscussionComposeModal", false);

  const [isReloading, setIsReloading] = useState(false);
  const prevIsValidating = useRef(false);

  const { infiniteList, mutate, prependItem, isValidating } = useInfiniteList(
    (pageIndex) =>
      discussionsUrl(pageIndex + 1, tab, { excludeBots: excludeBots }),
    {
      fetcher: (endpoint) =>
        baseFetcher(endpoint, undefined, {
          fetchOptions: { cache: "no-store" },
        }),
      swrOptions: {
        revalidateOnMount: true,
        revalidateIfStale: true,
      },
      hasMore: ({ data }) =>
        !data?.length || !!data?.[data?.length - 1]?.length,
      endIndicator: () => (
        <div className="text-center">No more discussion found</div>
      ),
      emptyIndicator: () => (
        <div className="text-center">No discussion found</div>
      ),
      loadingIndicator: ({ data }) => (
        <Loader numberOfItems={data?.length ? 1 : 6} className="flex-1" />
      ),
      pageRender(pageData, _index) {
        if (!pageData) return null;

        return pageData
          ?.filter?.((discussion: any) => {
            if (excludeBots) {
              return discussion?.commenter?.slug !== "hot-headlines";
            }

            return true;
          })
          ?.map?.((discussion: any) => (
            <DiscussionSnippet
              discussion={discussion}
              key={discussion.id}
              style={mobile || isCompactView ? "compactV2" : "v2"}
              showComposerByDefault
              noBorder
            />
          ));
      },
    }
  );

  useEffect(() => {
    if (prevIsValidating.current && !isValidating && isReloading) {
      setIsReloading(false);
    }
    prevIsValidating.current = isValidating;
  }, [isValidating, isReloading]);

  useEffectOnce(
    (acknowledge) => {
      if (!loggedInAccount) return;

      trackActivity("comment.view_thread", undefined, {
        type: "list",
        tab,
      });

      acknowledge();
    },
    [loggedInAccount, interfaceSetting, excludeBots]
  );

  const onCloseModal = useCallback(() => {
    const query = { ...router.query };
    delete query.id;

    router.replace({ query }, undefined, { shallow: true, scroll: false });
  }, [router]);

  const filterPills = useMemo(() => {
    const { pills } =
      (tabs.find(
        (t) => t !== "separator" && t.filters.includes(tab)
      ) as TabType) || {};

    if (!pills) return null;

    return (
      <Tabs className="pb-[15px]">
        {pills.map((pill) => (
          <Tab
            key={pill.filter}
            href={`/discussions?tab=${pill.filter}`}
            active={tab === pill.filter}
            onClick={() => {
              if (tab !== pill.filter) {
                trackActivity("comment.view_thread", undefined, {
                  type: "list",
                  tab: pill.filter,
                });
              }
            }}
          >
            {pill.name}
          </Tab>
        ))}
      </Tabs>
    );
  }, [tab, trackActivity]);

  if (!loggedInAccount?.canViewComment) {
    return (
      <CustomCard className="p-[15px] min-h-[calc(100vh-100px)]">
        <DiscussionRestrictionState textClassName="text-center italic" />
      </CustomCard>
    );
  }

  if (
    loggedInAccount.isPreviewPass &&
    !isPhoneNumberValidating &&
    !(phoneNumber?.verified || phoneNumber?.skipVerification)
  ) {
    return (
      <CustomCard className="p-[15px] min-h-[calc(100vh-100px)]">
        <UnverifiedPhoneNumber />
      </CustomCard>
    );
  }

  return (
    <>
      {filterPills}
      <CustomCard className="p-[15px] min-h-[calc(100vh-100px)]">
        <div className="flex md:hidden md:pt-[10px] mb-[10px] items-center justify-end space-x-[10px]">
          <SwitchButton
            checked={excludeBots}
            onClick={() => setExcludeBots(!excludeBots)}
            tooltip={"Activate to hide automated discussions"}
          >
            Focused
          </SwitchButton>
        </div>
        <div>
          {isReloading && (
            <Loader numberOfItems={1} className="flex-1 mb-[10px]" />
          )}
          {infiniteList}
        </div>
        {showDiscussionComposeModal && (
          <DiscussionComposeModal
            onClose={() => setShowDiscussionComposeModal(false)}
            onDiscussionPost={(newDiscussion) => {
              setIsReloading(true);
              if (newDiscussion) {
                prependItem(newDiscussion);
              } else {
                mutate();
              }
              setShowDiscussionComposeModal(false);
            }}
            style="v3"
          />
        )}
      </CustomCard>
      {router.query?.id && (
        <DiscussionModal
          id={router.query.id as string}
          onClose={onCloseModal}
          style="v2"
        />
      )}
    </>
  );
};

export default DiscussionPageBrowse;
